import React, { useMemo } from 'react';
import { Button, Message, Spin } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { useOnboardingStatus } from './hooks/useOnboardingData';
import OnboardingLayout from './OnboardingLayout';
import OnboardingWizard from './OnboardingWizard';
import AdminOnboardingWizard from './AdminOnboardingWizard';
import CorporateOnboardingWizard from './CorporateOnboardingWizard';
import StudentOnboardingWizard from './StudentOnboardingWizard';
import OnboardingRolePlaceholder from './OnboardingRolePlaceholder';

const TEACHER_ROLES = new Set(['teacher']);
const ADMIN_ROLES = new Set(['admin']);
const CORPORATE_ROLES = new Set(['corporate_contact']);
const STUDENT_ROLES = new Set(['student']);

const OnboardingRoleRouter = () => {
  const { t } = useTranslation('onboarding');
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const previewType = searchParams.get('preview');
  const previewMode = previewType === 'teacher' || previewType === 'admin' || previewType === 'corporate' || previewType === 'student';

  const {
    data: roleName,
    isLoading: loading,
    error: queryError,
    refetch: loadRole
  } = useOnboardingStatus({
    enabled: !previewMode,
    select: (data) => data?.role_name || null
  });

  const error = queryError ? t('roleRouter.loadError') : null;

  if (loading) {
    return (
      <OnboardingLayout>
        <div className="onboarding-loading">
          <Spin size={40} />
          <p>{t('roleRouter.loading')}</p>
        </div>
      </OnboardingLayout>
    );
  }

  if (error) {
    return (
      <OnboardingLayout
        currentStep={1}
        totalSteps={1}
        stepTitle={t('wizard.underConstruction')}
      >
        <div className="onboarding-wizard">
          <div className="wizard-content">
            <Message type="error" content={error} />
            <div style={{ marginTop: 16 }}>
              <Button type="primary" onClick={() => loadRole()}>{t('roleRouter.retry')}</Button>
            </div>
          </div>
        </div>
      </OnboardingLayout>
    );
  }

  if (previewMode) {
    if (previewType === 'admin') {
      return <AdminOnboardingWizard previewMode />;
    }
    if (previewType === 'corporate') {
      return <CorporateOnboardingWizard previewMode />;
    }
    if (previewType === 'student') {
      return <StudentOnboardingWizard previewMode />;
    }
    return <OnboardingWizard previewMode />;
  }

  if (TEACHER_ROLES.has(roleName)) {
    return <OnboardingWizard />;
  }

  if (ADMIN_ROLES.has(roleName)) {
    return <AdminOnboardingWizard />;
  }

  if (CORPORATE_ROLES.has(roleName)) {
    return <CorporateOnboardingWizard />;
  }

  if (STUDENT_ROLES.has(roleName)) {
    return <StudentOnboardingWizard />;
  }

  return <OnboardingRolePlaceholder roleName={roleName} />;
};

export default OnboardingRoleRouter;
